import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import type { ProjectElementDataWithWork } from '../../shared/dto/EntityRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { shrinkString } from '../../shared/util/shrinkString';
import WorkArtistListLine from '../work/WorkArtistListLine';
import { WorkContentMode } from '../../shared/enum/EnumRepository';

export const FLOOR_PLAN_PE_INDEX_DRAG_MIME_TYPE = 'text/plain';

const HANDLE_ICON = '::';
const TITLE_MAX = 35;

/** 항상 문자열 — API가 id를 숫자로 줄 때 Set/`dataTransfer` 키와 타입이 어긋나 드롭이 무시되는 것을 막음 */
export function floorPlanPeRowKey(pe: ProjectElementDataWithWork): string {
  return String(pe.id);
}

interface FloorPlanModalIndexingRowProps {
  item: ProjectElementDataWithWork;
  /** 0-based; 표시는 +1 */
  orderIndex: number;
  isDragging: boolean;
  isReorderPicked?: boolean;
  onToggleReorderPick?: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const FloorPlanModalIndexingRow: React.FC<FloorPlanModalIndexingRowProps> = ({
  item,
  orderIndex,
  isDragging,
  isReorderPicked = false,
  onToggleReorderPick,
  onDragStart,
  onDragEnd,
}) => {
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const resolvedUrl = item.work.uploadFile?.originUrl || '';
  const imageUrl = useOptimizedImageUrl(resolvedUrl, imgContainerRef);

  const shrinkedTitle = useMemo(
    () => shrinkString(item.work.title ?? '', TITLE_MAX),
    [item.work.title]
  );
  const orderLabel = String(orderIndex + 1);
  const rowKey = floorPlanPeRowKey(item);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(FLOOR_PLAN_PE_INDEX_DRAG_MIME_TYPE, rowKey);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart();
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  return (
    <Root
      data-floor-plan-index-row=""
      $isDragging={isDragging}
      $isReorderPicked={isReorderPicked}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onToggleReorderPick?.();
      }}
    >
      <DragBody draggable onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Column $flex="0.15" $mutedLabel $justifyContent="flex-start">
          <DragHandle>{HANDLE_ICON}</DragHandle>
          {orderLabel}
        </Column>
        <Column $flex="0.3">
          <ImgWrapper ref={imgContainerRef}>
            <ThumbImg
              draggable={false}
              src={imageUrl === '' ? defaultImg : imageUrl}
              alt={item.work.title ?? ''}
            />
          </ImgWrapper>
        </Column>
        <Column $flex="2.5" $justifyContent="flex-start" $paddingLeft="10px">
          <TitleStack>
            <WorkArtistListLine
              artist={item.work.artist}
              maxLength={TITLE_MAX}
              mode={WorkContentMode.Orig}
            />
            <span>{shrinkedTitle}</span>
          </TitleStack>
        </Column>
      </DragBody>
    </Root>
  );
};

const Root = styled.article<{ $isDragging: boolean; $isReorderPicked: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.typography.Body_03_2};
  color: ${({ theme }) => theme.colors.color_Gray_03};

  height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  min-height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleY(0.2) scaleX(0.5)' : 'scaleY(1) scaleX(1)'};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  transition: height 0.4s ease-out, transform 0.4s ease-out, opacity 0.4s ease-out;
  padding-left: 4px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  background-color: ${({ $isReorderPicked, theme }) =>
    $isReorderPicked ? theme.colors.color_Gray_06 : 'transparent'};
`;

const DragBody = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0;
  cursor: grab;

  &:hover {
    transform: scale(0.98);
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  margin-right: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const TitleStack = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Column = styled.div<{
  $flex: string;
  $justifyContent?: 'center' | 'flex-start';
  $paddingLeft?: string;
  $mutedLabel?: boolean;
}>`
  flex: ${({ $flex }) => $flex};
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: ${({ $justifyContent }) => $justifyContent ?? 'center'};
  padding-left: ${({ $paddingLeft }) => $paddingLeft ?? 0};

  user-select: none;
  color: ${({ theme, $mutedLabel }) =>
    $mutedLabel ? theme.colors.color_Gray_04 : 'inherit'};
  ${({ theme }) => theme.typography.Body_04};
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 75%;
  display: flex;
  align-items: center;
  justify-content: center;

  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbImg = styled.img`
  width: 60%;
  height: 80%;

  object-fit: contain;
`;

export default FloorPlanModalIndexingRow;
